import {IModel} from '~/lib/model';

export enum IncentiveType {
    REBATE = 'rebate',
    DISCOUNT = 'discount',
    CLAWBACK = 'clawback',
    VENDOR = 'vendor',
    MEDIATION = 'mediation',
    SUPPLY_EXPENSE = 'supply_expense',
    UPSTREAM_FEE = 'upstream_fee'
}

export enum IncentiveStructure {
    FIXED_FEE = 'fixed_fee',
    GROSS_SPEND_TIERS = 'gross_spend_tiers',
    SELLER_INCOME_TIERS = 'seller_income_tiers',
    CPM = 'cpm'
}

export enum IncentiveState {
    ACTIVE = 'active',
    INACTIVE = 'inactive',
    CLOSED = 'closed'
}

export enum ValueUnit {
    USD = 'USD',
    PERCENT = 'PERCENT'
}

export type BusinessIncentiveScope = string | Record<string, unknown> | null;

export interface ITierConfig {
    name: string;
    thresholdUsd: number;
    percentage: number;
}

export interface IBusinessIncentive extends IModel {
    id: number | null;
    incentiveType: IncentiveType;
    structure: IncentiveStructure;
    scope: BusinessIncentiveScope;
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    state: IncentiveState;
    currentTier: string | null;
    tiers: ITierConfig[];
    value: number | null;
    valueUnit: ValueUnit | null;
}

export interface IBusinessIncentiveCreateRequest {
    incentiveType: IncentiveType;
    structure: IncentiveStructure;
    scope: string;
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    state?: IncentiveState;
    currentTier?: string | null;
    tiers?: ITierConfig[];
    value?: number | null;
}

export interface IBusinessIncentiveUpdateRequest {
    name?: string;
    description?: string;
    scope?: string;
    state?: IncentiveState;
    currentTier?: string | null;
    tiers?: ITierConfig[];
    value?: number | null;
    endDate?: string;
}

export const tierBasedIncentiveStructures = [
    IncentiveStructure.GROSS_SPEND_TIERS,
    IncentiveStructure.SELLER_INCOME_TIERS
] as const;

export const structureRequiresTier = (structure: IncentiveStructure): boolean =>
    tierBasedIncentiveStructures.includes(structure as (typeof tierBasedIncentiveStructures)[number]);

export const structureRequiresValue = (structure: IncentiveStructure): boolean =>
    !structureRequiresTier(structure);

export const incentiveTypeLabel: Record<IncentiveType, string> = {
    [IncentiveType.REBATE]: 'Rebate',
    [IncentiveType.DISCOUNT]: 'Discount',
    [IncentiveType.CLAWBACK]: 'Clawback',
    [IncentiveType.VENDOR]: 'Vendor',
    [IncentiveType.MEDIATION]: 'Mediation',
    [IncentiveType.SUPPLY_EXPENSE]: 'Supply expense',
    [IncentiveType.UPSTREAM_FEE]: 'Upstream fee'
};

export const incentiveStructureLabel: Record<IncentiveStructure, string> = {
    [IncentiveStructure.FIXED_FEE]: 'Fixed fee',
    [IncentiveStructure.GROSS_SPEND_TIERS]: 'Gross spend tiers',
    [IncentiveStructure.SELLER_INCOME_TIERS]: 'Seller income tiers',
    [IncentiveStructure.CPM]: 'CPM'
};

export const incentiveStateLabel: Record<IncentiveState, string> = {
    [IncentiveState.ACTIVE]: 'Active',
    [IncentiveState.INACTIVE]: 'Inactive',
    [IncentiveState.CLOSED]: 'Closed'
};
